import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DAILY_NOTE, useDaily } from "@/components/MessageTools";
import { logWarn } from "@/lib/log";
import { watchSilence } from "@/lib/silence";

async function startRecorder(onBlob) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = ["audio/webm", "audio/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
  const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks = [];
  const watch = watchSilence(stream, () => r.state !== "inactive" && r.stop());
  r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  r.onstop = () => {
    watch.close();
    stream.getTracks().forEach((t) => t.stop());
    const type = r.mimeType || "audio/webm";
    onBlob(new Blob(chunks, { type }), type.includes("mp4") ? "mp4" : "webm");
  };
  r.start();
  return r;
}

async function transcribe(blob, ext) {
  const fd = new FormData();
  fd.append("file", blob, `speech.${ext}`);
  const { data } = await http.post("/transcribe", fd);
  return data.text;
}

function useMicRecorder(onText, refreshEnt) {
  const [state, setState] = useState("idle");
  const rec = useRef(null);
  const onBlob = async (blob, ext) => {
    setState("transcribing");
    try {
      const text = await transcribe(blob, ext);
      if (text) onText(text);
      else toast.error("I didn't catch that. Try again.");
    } catch (e) { if (e.response?.status !== 402) toast.error(formatError(e)); } finally { setState("idle"); refreshEnt(); }
  };
  const start = async () => {
    try { rec.current = await startRecorder(onBlob); setState("recording"); } catch (e) {
      logWarn("Microphone unavailable", e);
      toast.error("Microphone access is blocked. Allow it in your browser to speak your question.");
    }
  };
  return { state, start, stop: () => rec.current?.stop() };
}

function MicIcon({ state }) {
  if (state === "transcribing") return <Loader2 className="h-4 w-4 animate-spin" />;
  return state === "recording" ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />;
}

export function MicButton({ onText, disabled, trigger = 0, hideWhenIdle = false }) {
  const { refreshEnt } = useAuth();
  const { hint, blocked, left } = useDaily("voice");
  const { state, start, stop } = useMicRecorder(onText, refreshEnt);
  const click = () => {
    if (state === "recording") return stop();
    if (state !== "idle") return;
    if (blocked) { toast.error(DAILY_NOTE); return; }
    start();
  };
  const recording = state === "recording";
  const clickRef = useRef(click);
  clickRef.current = click;
  useEffect(() => { if (trigger) clickRef.current(); }, [trigger]);
  if (hideWhenIdle && state === "idle") return null;
  return (
    <button onClick={click} disabled={disabled || state === "transcribing"} data-testid="mic-btn" title={recording ? "Stop recording" : `Speak your question${hint ? ` · ${hint}` : ""}`}
      className={`relative h-9 w-9 rounded-xl flex items-center justify-center transition-colors ${recording ? "bg-red-500/20 text-red-300 animate-pulse" : "text-neutral-400 hover:text-white hover:bg-white/10"}`}>
      <MicIcon state={state} />
      {left !== null && state === "idle" && (
        <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[9px] leading-4 text-center ${blocked ? "bg-yellow-300 text-black" : "bg-white/10 text-neutral-300"}`} data-testid="mic-daily-left">{left}</span>
      )}
    </button>
  );
}
